// ---------------------------------------------------------------------------
// Model dropdown — tenant-enabled feature for picking the per-session LLM.
// ---------------------------------------------------------------------------
// Mirrors `agent-dropdown.tsx` exactly in structure and styling. It can be
// rendered as a standalone control or as a submenu inside a conversation
// actions menu. The parent gates it on
// `modelSwitcherEnabled = canSwitchModel(me?.model_switcher_enabled)`.
//
// Backend contract (see `chat-bridge/src/api-proxy.ts: createModelsRouter`):
//   GET    /api/models/switchable?agentId=…&sessionId=…
//   GET    /api/models/current?agentId=…&sessionId=…
//   PUT    /api/models/current?agentId=…&sessionId=…  body { model: <slug> }
//   DELETE /api/models/current?agentId=…&sessionId=…
//
// Disabled or unknown tenants are rejected with 403; the surface here
// degrades to "Model switching is not available for your tenant" in that case.
// ---------------------------------------------------------------------------

import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, ChevronDown, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  useSwitchableModels,
  useSessionModel,
  useSwitchSessionModel,
} from "@/hooks/use-models";

export interface ModelDropdownProps {
  agentId: string | null;
  sessionId: string | null;
  getToken: () => Promise<string>;
  className?: string;
  /**
   * Render a compact standalone trigger. Embedded callers also use this to
   * enable the inline mobile picker while retaining the desktop submenu.
   */
  compact?: boolean;
  /** Render as a submenu of an existing DropdownMenu instead of a standalone trigger. */
  embedded?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ModelDropdown({
  agentId,
  sessionId,
  getToken,
  className,
  compact = false,
  embedded = false,
}: ModelDropdownProps) {
  const [open, setOpen] = useState(false);
  const inlinePanelId = useId();
  const { t } = useTranslation();
  // Compact remains explicit for standalone pickers. Embedded callers can
  // pair it with responsive utilities to use an inline mobile picker.
  const isCompact = compact;

  const {
    data: switchable,
    loading: loadingSwitchable,
    error: switchableError,
    refresh: refreshSwitchable,
  } = useSwitchableModels(agentId, sessionId, getToken);

  const {
    data: sessionModel,
    loading: loadingSession,
    error: sessionError,
    refresh: refreshSession,
  } = useSessionModel(agentId, sessionId, getToken);

  const { switching, switchModel, resetModel } =
    useSwitchSessionModel(getToken);

  // Defensive: parent already gates this, but if we ever render without an
  // (agent, session) pair there is nothing meaningful to show.
  if (!agentId || !sessionId) return null;

  const loading = loadingSwitchable || loadingSession || switching;
  const error = switchableError ?? sessionError;

  const currentSlug = sessionModel?.model ?? switchable?.current ?? null;
  const isOverride = sessionModel?.is_override ?? false;
  const isFreshSession = switchable?.group === "all";

  // `available` always includes the current slug; dedupe defensively.
  const available = Array.from(
    new Set(switchable?.available ?? (currentSlug ? [currentSlug] : [])),
  );

  const refresh = async () => {
    await Promise.all([refreshSwitchable(), refreshSession()]);
  };

  const handleSelect = async (slug: string) => {
    if (slug === currentSlug) return; // no-op
    const result = await switchModel(agentId, sessionId, slug);
    if (!result.ok) {
      console.warn("[ui] model switch failed:", result);
      toast.error(t("modelSwitcher.switchFailed", { status: result.status }));
    }
    await refresh();
  };

  const handleReset = async () => {
    const result = await resetModel(agentId, sessionId);
    if (!result.ok) {
      console.warn("[ui] model reset failed:", result);
      toast.error(t("modelSwitcher.switchFailed", { status: result.status }));
    }
    await refresh();
  };

  const menuItems = error ? (
    <div className="flex items-center gap-2 px-2 py-2 text-xs text-destructive">
      <AlertCircle className="size-4" />
      <span>{error}</span>
    </div>
  ) : (
    <>
      <DropdownMenuGroup>
        <DropdownMenuLabel className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          <span className="shrink-0">{t("modelSwitcher.label")}</span>
          {currentSlug && (
            <span className="min-w-0 truncate font-mono font-semibold text-foreground">
              {currentSlug}
            </span>
          )}
        </DropdownMenuLabel>

        {available.length === 0 ? (
          <div className="px-2 py-3 text-xs text-muted-foreground text-center">
            {t("modelSwitcher.listEmpty")}
          </div>
        ) : (
          available.map((slug) => {
            const isCurrent = slug === currentSlug;
            return (
              <DropdownMenuItem
                key={slug}
                className={cn(
                  "flex items-center gap-2 text-xs font-mono",
                  isCurrent && "bg-accent font-semibold",
                )}
                onSelect={async (e) => {
                  // Keep dropdown open across the async action so the user
                  // sees the optimistic highlight (UX feedback). The refresh
                  // below closes the implicit open state on the next render.
                  e.preventDefault();
                  await handleSelect(slug);
                }}
                disabled={switching}
              >
                <Sparkles
                  className={cn(
                    "size-3.5 shrink-0",
                    isCurrent
                      ? "text-amber-500"
                      : "text-muted-foreground",
                  )}
                />
                <span className="truncate">{slug}</span>
                {isCurrent && (
                  <span className="ml-auto text-[10px] text-muted-foreground">
                    {t("modelSwitcher.current")}
                  </span>
                )}
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuGroup>

      {switchable?.unavailable && switchable.unavailable.length > 0 && (
        <>
          <DropdownMenuSeparator />
          <div className="px-2 py-2 text-xs text-muted-foreground">
            {t("modelSwitcher.apiKeyRequired", { count: switchable.unavailable.length })} {" "}
            <a href="/connections#api-keys" className="text-primary underline underline-offset-2">
              {t("modelSwitcher.openApiKeys")}
            </a>
          </div>
        </>
      )}

      {isOverride && sessionModel && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="flex items-center gap-2 text-xs text-muted-foreground"
            onSelect={async (e) => {
              e.preventDefault();
              await handleReset();
            }}
            disabled={switching}
          >
            <span>
              {t("modelSwitcher.resetToDefault", {
                default: sessionModel.default,
              })}
            </span>
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  const embeddedTriggerContents = (
    <>
      {loading ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <Sparkles className="size-3.5 text-amber-500" />
      )}
      <span className="min-w-0 flex-1 truncate">
        {t("modelSwitcher.label")}
        {currentSlug ? `: ${currentSlug}` : ""}
      </span>
      {isOverride && (
        <span
          aria-hidden
          className="rounded-sm bg-amber-500/15 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300"
          title={t("modelSwitcher.tooltipOverride")}
        >
          {t("modelSwitcher.overrideBadge")}
        </span>
      )}
    </>
  );

  if (embedded) {
    return (
      <>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            className={cn(
              "min-w-[14rem] text-xs",
              isCompact && "hidden md:flex",
            )}
          >
            {embeddedTriggerContents}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent
            className={cn(
              "w-72 max-h-[min(calc(100dvh_-_1rem),var(--radix-dropdown-menu-content-available-height))] overflow-y-auto",
              isCompact && "hidden md:block",
            )}
          >
            {menuItems}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {isCompact && (
          <>
            <DropdownMenuItem
              className="w-full min-w-0 text-xs max-md:flex md:hidden"
              aria-controls={open ? inlinePanelId : undefined}
              aria-expanded={open}
              onSelect={(event) => {
                event.preventDefault();
                setOpen((isOpen) => !isOpen);
              }}
            >
              {embeddedTriggerContents}
              <ChevronDown
                className={cn(
                  "ml-auto size-3.5 text-muted-foreground transition-transform",
                  open && "rotate-180",
                )}
              />
            </DropdownMenuItem>

            {open && (
              <div
                id={inlinePanelId}
                className="mx-1 mb-1 rounded-sm border bg-muted/30 p-1 max-md:block md:hidden"
              >
                {menuItems}
              </div>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn("min-w-0 max-w-full", className)}>
            <DropdownMenu open={open} onOpenChange={setOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-8 min-w-0 max-w-full gap-1.5 px-2.5 text-xs font-medium",
                    isCompact && "w-8 shrink-0 justify-center px-0",
                    loading && "opacity-60 cursor-progress",
                  )}
                  disabled={!!error}
                  aria-label={t("modelSwitcher.title")}
                  title={t("modelSwitcher.title")}
                >
                  {loading ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="size-3.5 text-amber-500" />
                  )}
                  {!isCompact && (
                    <>
                      <span className="min-w-0 max-w-[140px] truncate">
                        {t("modelSwitcher.label")}
                        {currentSlug ? `: ${currentSlug}` : ""}
                      </span>
                      {isOverride && (
                        <span
                          aria-hidden
                          className="ml-0.5 rounded-sm bg-amber-500/15 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300"
                          title={t("modelSwitcher.tooltipOverride")}
                        >
                          {t("modelSwitcher.overrideBadge")}
                        </span>
                      )}
                      <ChevronDown className="size-3 text-muted-foreground" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-72 max-h-[min(calc(100dvh_-_1rem),var(--radix-dropdown-menu-content-available-height))] overflow-y-auto">
                {menuItems}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TooltipTrigger>
        {!error && currentSlug && switchable?.group && (
          <TooltipContent side="bottom" align="end">
            <p className="max-w-[220px] text-xs">
              {isFreshSession
                ? t("modelSwitcher.tooltipAllModels")
                : t("modelSwitcher.tooltipGroup", { group: switchable.group })}
            </p>
          </TooltipContent>
        )}
        {error && (
          <TooltipContent side="bottom" align="end">
            <p className="max-w-[220px] text-xs text-destructive">
              {error}
            </p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}
